import { paginationConfig } from '@mkp/shared/util-pagination';
import { authApiActions } from '@mkp/auth/actions';
import { incompleteUserActions } from '@mkp/onboarding/feature-incomplete/actions';
import { createEntityFeature, EntityFeatureState } from '@mkp/shared/util-state';
import { UnsubscribePageActions } from '@mkp/user/feature-unsubscribe/actions';
import { Action, createReducer, on } from '@ngrx/store';
import { languageEffectsActions } from '@mkp/core/state/actions';
import { User } from '@mkp/user/models';
import { userApiActions, userDetailsActions, userListActions } from '@mkp/user/state/actions';
export interface State extends EntityFeatureState<User, { message: string }> {
  loggedInId: User['id'] | null;
  list: User['id'][];
  listSize: number;
}

const {
  featureKey,
  initialState: initialFeatState,
  updaters,
  selectors,
  adapter,
} = createEntityFeature<State, 'userState'>('userState');
const initialState: State = {
  ...initialFeatState,
  loggedInId: null,
  list: [],
  listSize: paginationConfig.initialLimit,
};

const _reducer = createReducer(
  { ...initialState },
  on(userListActions.reset, (state) => ({ ...state, list: state.list.slice(0, state.listSize) })),
  on(userApiActions.readLoggedInUserSuccess, (state, { user: entity }) => ({
    ...updaters.getOneSuccess(state, { entity }),
    loggedInId: entity.id,
  })),
  on(userApiActions.resetPasswordSuccess, updaters.setLoaded),
  on(
    userApiActions.listUsersFailure,
    userApiActions.loadMoreUsersFailure,
    userApiActions.updateUserFailure,
    userApiActions.createUserOnAccountFailure,
    userApiActions.resetPasswordFailure,
    userApiActions.readLoggedInUserFailure,
    userApiActions.loadUsersByIdsFailure,
    userApiActions.updateIncompleteUserFailure,
    updaters.setError
  ),
  on(userApiActions.updateUserNoChanges, updaters.setLoaded),
  on(userApiActions.listUsersSuccess, (state, { users, totalCount, _links, filter }) => ({
    ...updaters.getPaginatedListSuccess(state, { entities: users, totalCount, _links, filter }),
    list: removeDuplicates(users.map((user) => user.id)),
  })),
  on(userApiActions.loadMoreUsersSuccess, (state, { users, totalCount, _links, filter }) => ({
    ...updaters.getPaginatedListSuccess(state, { entities: users, totalCount, _links, filter }),
    list: removeDuplicates(state.list.concat(users.map((user) => user.id))),
  })),
  on(
    userListActions.init,
    userListActions.loadMore,
    userDetailsActions.updateUser,
    userDetailsActions.updateUserProfile,
    userDetailsActions.createUserOnAccount,
    userDetailsActions.resetPassword,
    authApiActions.fetchAccessTokenSuccess,
    languageEffectsActions.appLanguageSwitchedForUser,
    UnsubscribePageActions.unsubscribeLoggedInUser,
    incompleteUserActions.updateUser,
    updaters.setLoading
  ),
  on(
    userApiActions.updateUserSuccess,
    userDetailsActions.marketingConsentUpdated,
    (state, { id, changes }) =>
      updaters.updateSuccess(state, { updateEntity: { id: id as string, changes } })
  ),
  on(userApiActions.updateIncompleteUserSuccess, (state, { user }) =>
    updaters.updateSuccess(state, { updateEntity: { id: user.id, changes: user } })
  ),
  on(userApiActions.createUserOnAccountSuccess, (state, { user }) =>
    updaters.createSuccess(state, { entity: user })
  ),
  on(userApiActions.loadUsersByIdsSuccess, (state, { users }) => {
    return adapter.upsertMany(users, { ...state });
  })
);

function reducer(state: State | undefined, action: Action): State {
  return _reducer(state, action);
}

export { featureKey, initialState, reducer, selectors };

const removeDuplicates = (array: string[]): string[] => Array.from(new Set(array));
